import type { ColumnSort } from "@tanstack/react-table"
import { useMemo } from "react"

import type { Instruction, InstructionSortField } from "@/graphql/codegen/graphql"
import { SortingOrder } from "@/graphql/codegen/graphql"
import useInstructionsForProductList from "@/screens/Products/components/Product/Instructions/hooks/useInstructionsForProductList"
import type { AutocompleteOption } from "@/types/form-fields"

interface UseParametrizedInstructionsForProductListReturn {
  instructions: Instruction[]
  fetchingInstructions: boolean
}

const useParametrizedInstructionsForProductList = ({
  searchQuery,
  sorting,
}: {
  searchQuery: string | AutocompleteOption
  sorting: ColumnSort[]
}): UseParametrizedInstructionsForProductListReturn => {
  const isSearchQueryString = typeof searchQuery === "string"

  // Search query can be a string or an exact instruction selected in suggestion list
  const filter = isSearchQueryString ? { query: searchQuery } : { instructionId: searchQuery.id }
  const sort = useMemo(() => {
    const sortData = sorting[0]
    if (!sortData) {
      return
    }
    return {
      field: sortData.id.toUpperCase() as InstructionSortField,
      order: sortData.desc ? SortingOrder.Desc : SortingOrder.Asc,
    }
  }, [sorting])

  const { instructions, fetchingInstructions } = useInstructionsForProductList({
    ...filter,
    sort,
    isActive: true,
    pause: !(isSearchQueryString ? searchQuery.length > 1 : !!searchQuery?.id),
  })

  return {
    instructions,
    fetchingInstructions,
  }
}

export default useParametrizedInstructionsForProductList
